import { useEffect, useState } from "react";
import { api, formatErr } from "../lib/api";
import { toast } from "sonner";
import { ShieldCheck, MagnifyingGlass, Trash, Broadcast, ArrowLeft, Image as ImageIcon, UploadSimple } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { PRESET_WALLPAPERS } from "../components/goLiveData";

export default function LiveSettings() {
    const nav = useNavigate();
    const [mods, setMods] = useState([]);
    const [search, setSearch] = useState("");
    const [searchResults, setSearchResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [defaultWp, setDefaultWp] = useState(localStorage.getItem("wta:defaultWallpaper") || "");
    const [uploadingWp, setUploadingWp] = useState(false);
    const [eligibility, setEligibility] = useState(null);

    useEffect(() => {
        api.get("/users/me/moderators").then(({ data }) => setMods(data)).catch(() => {});
        api.get("/live-eligibility").then(({ data }) => setEligibility(data)).catch(() => {});
    }, []);

    useEffect(() => {
        if (!search.trim()) { setSearchResults([]); return; }
        let cancelled = false;
        setSearching(true);
        const t = setTimeout(() => {
            api.get(`/users/search?q=${encodeURIComponent(search.trim())}`)
                .then(({ data }) => { if (!cancelled) setSearchResults(data.slice(0, 8)); })
                .catch(() => {})
                .finally(() => { if (!cancelled) setSearching(false); });
        }, 250);
        return () => { cancelled = true; clearTimeout(t); };
    }, [search]);

    const addMod = async (u) => {
        try {
            await api.post("/users/me/moderators", { user_id: u.id });
            toast.success(`@${u.name} added as moderator`);
            setSearch("");
            setSearchResults([]);
            const { data } = await api.get("/users/me/moderators");
            setMods(data);
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const removeMod = async (uid) => {
        if (!window.confirm("Revoke this moderator? They will not be added to your future live streams.")) return;
        try {
            await api.delete(`/users/me/moderators/${uid}`);
            setMods((arr) => arr.filter((m) => m.user.id !== uid));
            toast.success("Moderator revoked");
        } catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
    };

    const onUploadWp = async (e) => {
        const f = e.target.files?.[0];
        e.target.value = "";
        if (!f) return;
        if (!f.type.startsWith("image/")) return toast.error("Pick an image");
        if (f.size > 8 * 1024 * 1024) return toast.error("Max 8MB");
        setUploadingWp(true);
        try {
            const form = new FormData();
            form.append("file", f);
            const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
            const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
            setDefaultWp(url);
            localStorage.setItem("wta:defaultWallpaper", url);
            toast.success("Default wallpaper saved");
        } catch (err) {
            toast.error(formatErr(err.response?.data?.detail));
        } finally {
            setUploadingWp(false);
        }
    };

    const pickWp = (url) => {
        setDefaultWp(url);
        localStorage.setItem("wta:defaultWallpaper", url);
        toast.success("Default wallpaper saved");
    };

    return (
        <div className="px-4 md:px-10 py-8 max-w-3xl mx-auto">
            <button data-testid="lsp-back" onClick={() => nav(-1)} className="flex items-center gap-1 text-sm text-wta-muted hover:text-white mb-4">
                <ArrowLeft size={14} /> Back
            </button>
            <div className="flex items-center gap-2 mb-1">
                <Broadcast size={22} weight="fill" className="text-wta-primary" />
                <h1 className="font-heading text-4xl md:text-5xl tracking-tight uppercase">Live Settings</h1>
            </div>
            <p className="text-wta-muted text-sm mb-8">Configure moderators and defaults that apply to every stream you host.</p>

            {/* Eligibility */}
            {eligibility && (
                <div className={`text-sm rounded-sm px-4 py-3 border mb-8 ${eligibility.eligible ? "bg-wta-primary/10 border-wta-primary/40 text-wta-primary" : "bg-red-500/10 border-red-500/40 text-red-300"}`}>
                    {eligibility.live_disabled ? (
                        <>Live streaming suspended: {eligibility.live_disabled_reason}. Submit an appeal in your profile.</>
                    ) : eligibility.verified_followers === -1 ? (
                        <>Admin override: live access enabled.</>
                    ) : eligibility.eligible ? (
                        <>Eligible to go live · {eligibility.verified_followers} face-verified followers.</>
                    ) : (
                        <>Face-verified followers: <b>{eligibility.verified_followers}</b> / {eligibility.required} required.</>
                    )}
                </div>
            )}

            {/* Moderators section */}
            <section className="bg-wta-surface border border-wta-border rounded-sm p-5">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck size={18} weight="fill" className="text-wta-primary" />
                    <h2 className="font-heading text-2xl tracking-wider uppercase">Moderators</h2>
                </div>
                <p className="text-xs text-wta-muted mb-4">Moderators you add are auto-applied to every live stream you host until you revoke them.</p>

                <div className="relative max-w-md">
                    <input
                        data-testid="lsp-mod-search"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search users by name…"
                        className="w-full bg-wta-surface2 border border-wta-border rounded-sm pl-9 pr-3 py-2.5 text-sm"
                    />
                    <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-wta-muted" />
                </div>
                {searching && <div className="text-xs text-wta-muted mt-2">Searching…</div>}
                {searchResults.length > 0 && (
                    <div className="mt-3 grid sm:grid-cols-2 gap-2">
                        {searchResults.map((u) => (
                            <button
                                key={u.id}
                                type="button"
                                data-testid={`lsp-mod-add-${u.id}`}
                                onClick={() => addMod(u)}
                                className="flex items-center gap-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2 text-left hover:border-wta-primary"
                            >
                                <div className="w-8 h-8 rounded-full bg-black/40 overflow-hidden flex items-center justify-center text-xs font-heading">
                                    {u.profile_pic ? <img src={u.profile_pic} alt="" className="w-full h-full object-cover" /> : u.name?.[0]?.toUpperCase()}
                                </div>
                                <div className="text-sm truncate">@{u.name}</div>
                                <span className="ml-auto text-xs text-wta-primary">Add</span>
                            </button>
                        ))}
                    </div>
                )}

                <div className="mt-6">
                    <div className="text-xs tracking-widest text-wta-muted uppercase mb-2">Active Moderators ({mods.length})</div>
                    {mods.length === 0 ? (
                        <div className="text-sm text-wta-muted">No moderators yet. Search above to add your first.</div>
                    ) : (
                        <div className="grid sm:grid-cols-2 gap-2">
                            {mods.map((m) => (
                                <div key={m.user.id} data-testid={`lsp-mod-row-${m.user.id}`} className="flex items-center gap-2 bg-wta-surface2 border border-wta-border rounded-sm px-3 py-2">
                                    <div className="w-8 h-8 rounded-full bg-black/40 overflow-hidden flex items-center justify-center text-xs font-heading shrink-0">
                                        {m.user.profile_pic ? <img src={m.user.profile_pic} alt="" className="w-full h-full object-cover" /> : m.user.name?.[0]?.toUpperCase()}
                                    </div>
                                    <div className="text-sm flex-1 truncate">@{m.user.name}</div>
                                    <button
                                        data-testid={`lsp-mod-remove-${m.user.id}`}
                                        onClick={() => removeMod(m.user.id)}
                                        className="text-wta-muted hover:text-red-400 p-1"
                                        aria-label="Revoke"
                                    >
                                        <Trash size={14} />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </section>

            {/* Default wallpaper */}
            <section className="bg-wta-surface border border-wta-border rounded-sm p-5 mt-6">
                <div className="flex items-center gap-2 mb-1">
                    <ImageIcon size={18} weight="fill" className="text-wta-primary" />
                    <h2 className="font-heading text-2xl tracking-wider uppercase">Default Wallpaper</h2>
                </div>
                <p className="text-xs text-wta-muted mb-4">Pre-select a wallpaper to use whenever you go live without your camera.</p>

                <div className="grid grid-cols-4 gap-2 max-w-md">
                    {PRESET_WALLPAPERS.map((w) => (
                        <button
                            key={w.id}
                            type="button"
                            data-testid={`lsp-wp-${w.id}`}
                            onClick={() => pickWp(w.url)}
                            className={`aspect-video rounded-sm overflow-hidden border-2 ${defaultWp === w.url ? "border-wta-primary" : "border-wta-border"}`}
                        >
                            <img src={w.url} alt={w.label} className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
                <label className="btn-ghost mt-3 inline-flex items-center gap-2 px-3 py-2 rounded-sm text-xs cursor-pointer">
                    <UploadSimple size={12} /> {uploadingWp ? "Uploading…" : "Upload custom"}
                    <input data-testid="lsp-wp-upload" type="file" accept="image/*" onChange={onUploadWp} className="hidden" />
                </label>
                {defaultWp && (
                    <div className="mt-3">
                        <div className="text-[10px] text-wta-muted mb-1">Currently selected:</div>
                        <img src={defaultWp} alt="" className="w-48 aspect-video object-cover rounded-sm border border-wta-border" />
                    </div>
                )}
            </section>
        </div>
    );
}
